import {
  isProviderAvailable,
  type OrchestrationSession,
  type ProviderDriverKind,
  type ProviderInstanceId,
  type ServerProvider,
} from "@afar-research/contracts";

type NativeConversationSession = Pick<OrchestrationSession, "providerInstanceId" | "status">;

const USABLE_NATIVE_CONVERSATION_STATUSES = new Set<OrchestrationSession["status"]>([
  "running",
  "ready",
]);

function isBoundNativeConversation(input: {
  readonly selectedProviderInstanceId: ProviderInstanceId;
  readonly session?: NativeConversationSession | null | undefined;
}): input is {
  readonly selectedProviderInstanceId: ProviderInstanceId;
  readonly session: NativeConversationSession;
} {
  return input.session?.providerInstanceId === input.selectedProviderInstanceId;
}

export function canContinueBoundNativeConversationAfterDiscoveryTimeout(input: {
  readonly selectedProviderInstanceId: ProviderInstanceId;
  readonly selectedProviderDriver: ProviderDriverKind;
  readonly discoveryFailure: ServerProvider["discoveryFailure"];
  readonly session?: NativeConversationSession | null | undefined;
}): boolean {
  return (
    input.selectedProviderDriver === "codex" &&
    input.discoveryFailure === "timeout" &&
    isBoundNativeConversation(input) &&
    USABLE_NATIVE_CONVERSATION_STATUSES.has(input.session.status)
  );
}

/** Every authoritative readiness axis must agree before prompt dispatch. */
export function isProviderInstanceFullyReady(provider: ServerProvider): boolean {
  const supportedOwnership =
    provider.ownership.type === "user-owned" ||
    (provider.ownership.type === "app-owned" && provider.driver === "codex");
  return (
    supportedOwnership &&
    provider.enabled &&
    isProviderAvailable(provider) &&
    provider.installed &&
    provider.installation === "installed" &&
    provider.launchability === "launchable" &&
    provider.auth.status === "authenticated" &&
    provider.readiness === "ready" &&
    provider.status === "ready"
  );
}

export function derivePromptProviderReadiness(input: {
  readonly selectedProviderInstanceId: ProviderInstanceId;
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly session?: NativeConversationSession | null;
}): {
  readonly selectedProviderReady: boolean;
  readonly noReadyProvider: boolean;
} {
  const selectedProvider = input.providers.find(
    (provider) => provider.instanceId === input.selectedProviderInstanceId,
  );
  // A failed native conversation does not make the provider unready. Blocking
  // on it stranded the thread: the composer told the user to choose another
  // ready provider, which is not a recovery, and no other control retried the
  // turn. The Environment Service already handles this case by starting a
  // fresh provider session, so the gate only has to answer whether the
  // provider can take a prompt. The discovery-timeout override stays narrow on
  // its own terms — it admits only a `running` or `ready` bound conversation,
  // so it can never resurrect a failed one.
  const selectedProviderReady =
    selectedProvider !== undefined &&
    (canContinueBoundNativeConversationAfterDiscoveryTimeout({
      selectedProviderInstanceId: input.selectedProviderInstanceId,
      selectedProviderDriver: selectedProvider.driver,
      discoveryFailure: selectedProvider.discoveryFailure,
      session: input.session,
    }) ||
      isProviderInstanceFullyReady(selectedProvider));
  const hasReadyProvider = input.providers.some(isProviderInstanceFullyReady);
  return {
    selectedProviderReady,
    noReadyProvider: !selectedProviderReady && !hasReadyProvider,
  };
}
